import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import InputField from "components/fields/InputField";
import Modal from "components/modal";
import { updateAnnouncement } from "store/announcementSlice";
import Switch from "components/switch";

function EditAnnouncementModal({ announcement, isOpen, onClose, refreshList }) {
  const dispatch = useDispatch();
  const { editLoading, editError } = useSelector(
    (state) => state.announcements
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      message: "",
      backgroundColor: "#FF0000",
      textColor: "#FFFFFF",
      isActive: true,
    },
    mode: "onBlur",
  });

  useEffect(() => {
    if (isOpen && announcement) {
      reset({
        message: announcement.message ?? "",
        backgroundColor: announcement.backgroundColor ?? "#FF0000",
        textColor: announcement.textColor ?? "#FFFFFF",
        isActive: announcement.isActive ?? true,
      });
    }
  }, [isOpen, announcement, reset]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        _id: announcement._id,
        message: data.message,
        backgroundColor: data.backgroundColor,
        textColor: data.textColor,
        isActive: data.isActive,
      };
      const response = await dispatch(updateAnnouncement(payload));
      if (
        response?.payload?.statuscode === 200 ||
        response?.payload?.statuscode === 201
      ) {
        refreshList?.();
        onClose();
        reset();
      }
    } catch (err) {
      console.error("Update announcement failed:", err);
    }
  };

  if (!announcement) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
        Edit Announcement
      </h2>

      <div className="mt-5 w-full flex-col items-center lg:pl-0">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mx-auto grid w-full grid-cols-1 gap-4"
          noValidate
        >
          <InputField
            id="edit-message"
            variant="auth"
            label="Message*"
            placeholder="Enter announcement message"
            type="text"
            error={errors.message}
            {...register("message", { required: "Message is required" })}
          />

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="backgroundColor"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                    Background Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="edit-backgroundColor"
                      value={field.value || "#FF0000"}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-10 w-14 cursor-pointer rounded border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
                    />
                    <span className="text-sm text-navy-700 dark:text-white">
                      {field.value || "#FF0000"}
                    </span>
                  </div>
                </div>
              )}
            />
            <Controller
              name="textColor"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                    Text Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="edit-textColor"
                      value={field.value || "#FFFFFF"}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-10 w-14 cursor-pointer rounded border border-gray-300 bg-transparent p-0.5 dark:border-gray-600"
                    />
                    <span className="text-sm text-navy-700 dark:text-white">
                      {field.value || "#FFFFFF"}
                    </span>
                  </div>
                </div>
              )}
            />
          </div>

          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <label className="ml-1.5 text-sm font-medium text-navy-700 dark:text-white">
                  Status
                </label>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <span className="text-sm text-navy-700 dark:text-white">
                    {field.value ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          />

          {editError && (
            <p className="text-sm text-red-500">{editError}</p>
          )}

          <div className="my-4 flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <LoaderButton type="submit" isLoading={editLoading}>
              Update Announcement
            </LoaderButton>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default EditAnnouncementModal;
